import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import MenuIcon from "@mui/icons-material/Menu";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import { Box, Button, IconButton, Stack, Typography } from "@mui/material";
import { Link } from "react-router-dom";
import type { UserContext } from "../../auth/auth";
import AsyncLoadingButton from "../../components/asyncLoadingButton/AsyncLoadingButton";
import ButtonWithContextMenu from "../../components/buttonWithContextMenu/ButtonWithContextMenu";
import { StatusChip } from "../components/StatusChip";
import type { CalculationBasicData } from "./BasicData";

export function NavigationBar(props: {
  onSubmitVersion(): Promise<void>;
  onSubmitOrder?(orderFromProviders: boolean): Promise<void>;
  onDelete?(): Promise<void>;
  onEdit?(): void;
  onDownloadPrices: () => Promise<void>;
  onGeneratePDF: () => Promise<void>;
  onCheckAvailability?: () => void;
  onUpdatePrices?: () => void;
  onRequestQuote?: () => Promise<void>;
  context: UserContext | undefined;
  isEditMode: boolean;
  isOrdered: boolean;
  hasProductsForQuote: boolean;
  calculation: CalculationBasicData;
  isNew: boolean;
  orderId?: string;
  saveDisabled: boolean;
}) {
  const context = props.context;
  const role = context?.role;
  const isAdminOrAgent = role === "ADMIN" || role === "AGENT";
  const onSubmitOrder = props.onSubmitOrder;
  const canOrderFromProvider =
    role === "ADMIN" || role === "AGENT" || (role === "CUSTOMER" && context?.customPrivileges.canOrderCalculation);
  const onRequestQuote = props.onRequestQuote;
  return (
    <>
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={2}
        sx={{
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", md: "center" },
          mb: 4,
          position: { xs: "static", md: "sticky" },
          top: { xs: 56, md: isAdminOrAgent ? 64 : 128 },
          zIndex: 10,
          bgcolor: "background.default",
          py: 2,
          mt: 0,
          borderBottom: "1px solid rgba(0,0,0,0.05)",
        }}
      >
        <Stack
          direction="row"
          spacing={2}
          sx={{
            alignItems: "center",
            mb: { xs: 3, md: 0 },
          }}
        >
          <IconButton component={Link} to="/calculations">
            <ArrowBackIcon />
          </IconButton>
          {props.isNew ? (
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                Nowa Kalkulacja
              </Typography>
            </Box>
          ) : (
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                Kalkulacja {props.calculation.name}
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 0.5, alignItems: "center" }}>
                <Typography variant="body2" color="textSecondary">
                  {props.calculation.startDate?.format("DD.MM.YYYY")} • {props.calculation.name}
                </Typography>
                <StatusChip orderId={props.orderId} />
              </Stack>
            </Box>
          )}
        </Stack>

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.5}
          sx={{ mt: { xs: 2, md: 0 }, width: { xs: "100%", sm: "auto" } }}
        >
          {!props.isOrdered && props.onDelete && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              sx={{ width: { xs: "100%", sm: "auto" } }}
              onClick={props.onDelete}
            >
              Usuń
            </Button>
          )}

          {!props.isEditMode && (
            <ButtonWithContextMenu
              loading={false}
              startIcon={<MenuIcon />}
              variant="outlined"
              color="secondary"
              sx={{ width: { xs: "100%", sm: "auto" } }}
              menuItems={[
                {
                  name: "Aktualizuj ceny",
                  disabled: !props.onUpdatePrices || props.isOrdered,
                  onClick: () => props.onUpdatePrices && props.onUpdatePrices(),
                },
                {
                  name: "Sprawdź dostępność",
                  disabled: !props.onCheckAvailability || props.isOrdered,
                  onClick: () => props.onCheckAvailability && props.onCheckAvailability(),
                },
                {
                  name: "Pobierz cennik",
                  disabled: false,
                  onClick: props.onDownloadPrices,
                },
                {
                  name: "Generuj PDF",
                  disabled: false,
                  onClick: props.onGeneratePDF,
                },
              ]}
            >
              Opcje
            </ButtonWithContextMenu>
          )}

          {!props.isEditMode && !props.isOrdered && (
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<EditIcon />}
              sx={{ width: { xs: "100%", sm: "auto" } }}
              onClick={props.onEdit}
            >
              Edytuj kalkulację
            </Button>
          )}

          {!props.isEditMode && !props.isOrdered && onRequestQuote && props.hasProductsForQuote && (
            <AsyncLoadingButton
              variant="contained"
              color="primary"
              startIcon={<ShoppingCartIcon />}
              disableElevation
              sx={{ width: { xs: "100%", sm: "auto" } }}
              onClick={() => onRequestQuote()}
            >
              Zamów wycenę
            </AsyncLoadingButton>
          )}

          {!props.isEditMode && !props.isOrdered && onSubmitOrder && canOrderFromProvider && (
            <AsyncLoadingButton
              variant="contained"
              color="primary"
              startIcon={<ShoppingCartIcon />}
              disableElevation
              sx={{ width: { xs: "100%", sm: "auto" } }}
              onClick={() => onSubmitOrder(true)}
            >
              Zamów u dostawcy
            </AsyncLoadingButton>
          )}

          {!props.isEditMode && !props.isOrdered && onSubmitOrder && (
            <AsyncLoadingButton
              variant="contained"
              color="primary"
              startIcon={<ShoppingCartIcon />}
              disableElevation
              sx={{ width: { xs: "100%", sm: "auto" } }}
              onClick={() => onSubmitOrder(false)}
            >
              Zamów
            </AsyncLoadingButton>
          )}
          {props.isEditMode && (
            <AsyncLoadingButton
              variant="contained"
              color="primary"
              data-testid="generate-calculation"
              disabled={props.saveDisabled}
              startIcon={<ShoppingCartIcon />}
              disableElevation
              sx={{ width: { xs: "100%", sm: "auto" } }}
              onClick={props.onSubmitVersion}
            >
              Wygeneruj kalkulację
            </AsyncLoadingButton>
          )}
        </Stack>
      </Stack>
    </>
  );
}
